<template>
    <div>
        <section class="section">
            <ul class="steps has-content-centered">
                <li
                    class="steps-segment"
                    :ref="'step1'"
                    :class="{ 'is-active': !environment }"
                >
                    <span class="steps-marker"></span>
                    <div class="steps-content">
                        <p class="is-size-4">{{ $t('Import.step1') }}</p>
                        <p>{{ $t('Import.step1_desc') }}</p>
                        <p v-if="environment" class="is-size-7 is-italic">
                            {{ $t('Import.step1_selected') + environment }}
                        </p>
                    </div>
                </li>
                <li
                    class="steps-segment"
                    :ref="'step2'"
                    :class="{ 'is-active': environment && !scenario }"
                >
                    <span class="steps-marker"></span>
                    <div class="steps-content">
                        <p class="is-size-4">{{ $t('Import.step2') }}</p>
                        <p>
                            {{ $t('Import.step2_desc') }}
                        </p>
                        <p v-if="scenario" class="is-size-7 is-italic">
                            {{ $t('Import.step2_selected') + scenarioName }}
                        </p>
                    </div>
                </li>
                <li
                    class="steps-segment"
                    :ref="'step3'"
                    :class="{ 'is-active': scenario && !confirmed }"
                >
                    <span class="steps-marker"></span>
                    <div class="steps-content">
                        <p class="is-size-4">{{ $t('Import.step3') }}</p>
                        <p>
                            {{ $t('Import.step3_desc') }}
                        </p>
                    </div>
                </li>
                <li
                    class="steps-segment"
                    :ref="'step4'"
                    :class="{ 'is-active': importFinished }"
                >
                    <span class="steps-marker"></span>
                    <div class="steps-content">
                        <p class="is-size-4">{{ $t('Import.step4') }}</p>
                        <p>{{ $t('Import.step4_desc') }}</p>
                    </div>
                </li>
            </ul>
        </section>

        <section class="section" v-if="!environment">
            <div class="columns">
                <div
                    v-for="content in availableEnvironments"
                    :key="content.name"
                    class="
                        column
                        is-12-mobile is-6-tablet is-4-desktop is-3-fullhd
                        lb-project-card lb-creation-object
                    "
                    @click="selectEnvironment(content)"
                >
                    <div class="lb-project-box">
                        <div
                            class="
                                columns
                                is-multiline is-mobile is-marginless
                                lb-project-background-card
                            "
                            :style="cardBackground(content.image)"
                        ></div>
                        <div class="columns is-mobile is-marginless pt-1">
                            <div class="column is-11 py-0 has-text-left pt-1">
                                <p
                                    class="
                                        is-size-6
                                        lb-card-title
                                        is-block
                                        has-text-black
                                    "
                                >
                                    {{ content.name }}
                                </p>
                                <p
                                    class="
                                        is-size-7
                                        lb-card-title
                                        is-block is-uppercase
                                    "
                                >
                                    {{ content.url }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section
            class="section is-small"
            v-if="environment && !availiableScenarios.length && !scenario"
        >
            <div class="loading-scenarios has-text-centered">
                <div class="spinner">
                    <div class="bounce1"></div>
                    <div class="bounce2"></div>
                    <div class="bounce3"></div>
                    <div class="bounce4"></div>
                    <div class="bounce5"></div>
                    <div class="bounce6"></div>
                </div>
                <p class="">
                    {{ $t('Import.get_scenarios_list_from') + environmentUrl }}
                </p>
            </div>
        </section>

        <section class="section" v-if="availiableScenarios.length && !scenario">
            <app-search-bar
                :placeholder="$t('search_text')"
                :text.sync="searchValue"
                class="lb-media-search-bar"
                @input="searchScenarios"
            />
            <div class="columns is-multiline">
                <div
                    v-for="scenario in visibleAvailableScenarios"
                    :key="scenario.id"
                    class="
                        column
                        is-12-mobile is-4-tablet is-2-desktop is-2-fullhd
                        lb-project-card lb-creation-object
                    "
                >
                    <div class="card is-radiusless scenario-card">
                        <div class="card-image">
                            <figure class="image is-3by2">
                                <img
                                    v-if="!isLabylok"
                                    loading="lazy"
                                    :src="
                                        environmentUrl +
                                        '/' +
                                        scenario.image.url
                                    "
                                    alt="Placeholder image"
                                />
                                <img
                                    v-else
                                    loading="lazy"
                                    :src="getImageUrl(scenario)"
                                    alt="Placeholder image"
                                />
                            </figure>
                        </div>
                        <div class="card-content">
                            <p class="has-text-weight-bold mb-2">
                                <span v-if="!isLabylok"
                                    >({{ scenario.id }})</span
                                >
                                <span class="is-clipped-1">{{
                                    scenario.title
                                }}</span>
                                <span v-if="isLabylok"
                                    >({{
                                        scenario.owner.alias ||
                                        scenario.owner.username
                                    }})</span
                                >
                            </p>

                            <div class="content">
                                <p class="is-size-7 has-text-grey is-clipped">
                                    {{ scenario.description }}
                                </p>
                            </div>
                        </div>
                        <footer class="card-footer">
                            <div class="card-footer-item py-0 px-0">
                                <button
                                    @click.stop="selectScenario(scenario)"
                                    class="
                                        button
                                        is-primary is-fullwidth is-radiusless
                                    "
                                >
                                    {{ $t('Import.import') }}
                                </button>
                            </div>
                            <div class="card-footer-item py-0 px-0">
                                <a
                                    :href="
                                        goToScenario(
                                            scenario.id || scenario._id
                                        )
                                    "
                                    target="_blank"
                                    class="
                                        button
                                        is-warning is-fullwidth is-radiusless
                                    "
                                >
                                    {{ $t('Import.view') }}
                                </a>
                            </div>
                        </footer>
                    </div>
                </div>
                <infinite-loading @infinite="infiniteHandler">
                    <div slot="spinner">
                        <div class="section is-small">
                            <div class="spinner">
                                <div class="bounce1"></div>
                                <div class="bounce2"></div>
                                <div class="bounce3"></div>
                                <div class="bounce4"></div>
                                <div class="bounce5"></div>
                                <div class="bounce6"></div>
                            </div>
                        </div>
                    </div>
                    <div slot="no-results"></div>
                    <div slot="no-more"></div>
                </infinite-loading>
            </div>
        </section>

        <section
            class="section is-small step3"
            v-if="availableObjectsType && !importFinished"
        >
            <div class="columns is-multiline" v-if="!isLabylok">
                <div v-if="points > 0" class="column is-4">
                    <div class="card">
                        <p
                            class="
                                card-header
                                has-background-primary has-text-white
                                px-4
                                py-4
                                has-text-weight-bold
                                is-size-5
                            "
                        >
                            <span class="icon mr-2 mt-1">
                                <i class="mdi mdi-24px mdi-map-marker"></i>
                            </span>
                            {{ $t('Import.points') }}
                        </p>
                        <div class="card-content">
                            <div
                                class="
                                    notification
                                    is-info is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{
                                        $t('Import.points_desc', {
                                            num: points,
                                        })
                                    }}
                                </p>
                            </div>

                            <p v-if="projectCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span
                                    v-html="
                                        $t('Import.project_created', {
                                            name: scenarioName,
                                        })
                                    "
                                />
                            </p>
                            <p v-else>
                                {{ $t('Import.project_to_create') }}
                                <span class="is-italic">{{
                                    scenarioName
                                }}</span>
                            </p>

                            <p v-if="pointsCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span class="mr-1">{{ points }}</span
                                >{{ $t('Import.points_created') }}
                            </p>
                            <p v-else>
                                <span
                                    v-html="
                                        $t('Import.points_to_create', {
                                            num: points,
                                        })
                                    "
                                />
                            </p>
                        </div>
                        <div class="card-footer px-4 py-4">
                            <div
                                class="level is-fullwidth"
                                v-if="!prcessingPoints"
                            >
                                <div class="level-left">
                                    <div class="level-item">
                                        <p>
                                            {{ $t('Import.process_objects') }}
                                        </p>
                                    </div>
                                </div>
                                <div class="level-right">
                                    <div class="level-item">
                                        <div class="buttons has-addons pb-2">
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        importPoints,
                                                }"
                                                @click="importPoints = true"
                                            >
                                                {{ $t('Import.yes') }}
                                            </button>
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        !importPoints,
                                                }"
                                                @click="importPoints = false"
                                            >
                                                {{ $t('Import.no') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else
                                class="py-4 px-2 is-fullwidth"
                                style="width: 100%"
                            >
                                <progress
                                    class="progress is-small"
                                    :value="pointsProcessed"
                                    max="100"
                                >
                                    20%
                                </progress>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="trivials >= 0" class="column is-4">
                    <div class="card">
                        <p
                            class="
                                card-header
                                has-background-primary has-text-white
                                px-4
                                py-4
                                has-text-weight-bold
                                is-size-5
                            "
                        >
                            <span class="icon is-small mr-2 mt-1">
                                <i class="mdi mdi-24px mdi-help"></i>
                            </span>
                            {{ $t('Import.trivials') }}
                        </p>
                        <div class="card-content">
                            <div
                                class="
                                    notification
                                    is-info is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{
                                        $t('Import.trivials_desc', {
                                            num: trivials,
                                        })
                                    }}
                                </p>
                            </div>

                            <p v-if="projectQuizCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span
                                    v-html="
                                        $t('Import.project_created', {
                                            name: scenarioName,
                                        })
                                    "
                                />
                            </p>
                            <p v-else>
                                {{ $t('Import.project_to_create') }}
                                <span class="is-italic">{{
                                    scenarioName
                                }}</span>
                            </p>

                            <p v-if="trivialsCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span class="mr-1">{{ trivials }}</span
                                >{{ $t('Import.points_created') }}
                            </p>
                            <p v-else>
                                <span
                                    v-html="
                                        $t('Import.points_to_create', {
                                            num: trivials,
                                        })
                                    "
                                />
                            </p>
                        </div>
                        <div class="card-footer px-4 py-4">
                            <div
                                class="level is-fullwidth"
                                v-if="!processingTrivials"
                            >
                                <div class="level-left">
                                    <div class="level-item">
                                        <p>
                                            {{ $t('Import.process_objects') }}
                                        </p>
                                    </div>
                                </div>
                                <div class="level-right">
                                    <div class="level-item">
                                        <div class="buttons has-addons pb-2">
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        importTrivials,
                                                }"
                                                @click="importTrivials = true"
                                            >
                                                {{ $t('Import.yes') }}
                                            </button>
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        !importTrivials,
                                                }"
                                                @click="importTrivials = false"
                                            >
                                                {{ $t('Import.no') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else
                                class="py-4 px-2 is-fullwidth"
                                style="width: 100%"
                            >
                                <progress
                                    class="progress is-small"
                                    :value="trivialsProcessed"
                                    max="100"
                                >
                                    20%
                                </progress>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="clueGames >= 0" class="column is-4">
                    <div class="card">
                        <p
                            class="
                                card-header
                                has-background-primary has-text-white
                                px-4
                                py-4
                                has-text-weight-bold
                                is-size-5
                            "
                        >
                            <span class="icon mr-2 mt-1">
                                <i class="mdi mdi-24px mdi-paw"></i>
                            </span>
                            {{ $t('Import.clue_games') }}
                        </p>
                        <div class="card-content">
                            <div
                                class="
                                    notification
                                    is-info is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{
                                        $t('Import.clue_games_desc', {
                                            num: clueGames,
                                        })
                                    }}
                                </p>
                            </div>

                            <p v-if="projectTreasurehuntCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span
                                    v-html="
                                        $t('Import.project_created', {
                                            name: scenarioName,
                                        })
                                    "
                                />
                            </p>
                            <p v-else>
                                {{ $t('Import.project_to_create') }}
                                <span class="is-italic">{{
                                    scenarioName
                                }}</span>
                            </p>

                            <p v-if="clueGamesCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span class="mr-1">{{ clueGames }}</span
                                >{{ $t('Import.points_created') }}
                            </p>
                            <p v-else>
                                <span
                                    v-html="
                                        $t('Import.points_to_create', {
                                            num: clueGames,
                                        })
                                    "
                                />
                            </p>
                        </div>
                        <div class="card-footer px-4 py-4">
                            <div
                                class="level is-fullwidth"
                                v-if="!processingClues"
                            >
                                <div class="level-left">
                                    <div class="level-item">
                                        <p>
                                            {{ $t('Import.process_objects') }}
                                        </p>
                                    </div>
                                </div>
                                <div class="level-right">
                                    <div class="level-item">
                                        <div class="buttons has-addons pb-2">
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        importClues,
                                                }"
                                                @click="importClues = true"
                                            >
                                                {{ $t('Import.yes') }}
                                            </button>
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        !importClues,
                                                }"
                                                @click="importClues = false"
                                            >
                                                {{ $t('Import.no') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else
                                class="py-4 px-2 is-fullwidth"
                                style="width: 100%"
                            >
                                <progress
                                    class="progress is-small"
                                    :value="cluesProcessed"
                                    max="100"
                                >
                                    20%
                                </progress>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="tracks >= 0" class="column is-4">
                    <div class="card">
                        <p
                            class="
                                card-header
                                has-background-primary has-text-white
                                px-4
                                py-4
                                has-text-weight-bold
                                is-size-5
                            "
                        >
                            <span class="icon mr-2 mt-1">
                                <i
                                    class="
                                        mdi mdi-24px mdi-chart-timeline-variant
                                    "
                                ></i>
                            </span>
                            {{ $t('Import.tracks') }}
                        </p>
                        <div class="card-content">
                            <div
                                class="
                                    notification
                                    is-info is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{
                                        $t('Import.tracks_desc', {
                                            num: tracks,
                                        })
                                    }}
                                </p>
                            </div>

                            <div
                                class="
                                    notification
                                    is-danger is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{ $t('Import.not_possible_to_import') }}
                                </p>
                            </div>
                        </div>
                        <div class="card-footer px-4 py-4">
                            <div class="level is-fullwidth">
                                <div class="level-left">
                                    <div class="level-item">
                                        <p>
                                            {{ $t('Import.process_objects') }}
                                        </p>
                                    </div>
                                </div>
                                <div class="level-right">
                                    <div class="level-item">
                                        <div
                                            class="
                                                buttons
                                                has-addons
                                                pb-2
                                                disabled
                                            "
                                        >
                                            <button
                                                class="button is-disabled"
                                                disabled
                                                :class="{
                                                    'is-primary is-selected':
                                                        importTracks,
                                                }"
                                                @click="importTracks = true"
                                            >
                                                {{ $t('Import.yes') }}
                                            </button>
                                            <button
                                                class="button is-disabled"
                                                disabled
                                                :class="{
                                                    'is-primary is-selected':
                                                        !importTracks,
                                                }"
                                                @click="importTracks = false"
                                            >
                                                {{ $t('Import.no') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="timeMachines >= 0" class="column is-4">
                    <div class="card">
                        <p
                            class="
                                card-header
                                has-background-primary has-text-white
                                px-4
                                py-4
                                has-text-weight-bold
                                is-size-5
                            "
                        >
                            <span class="icon mr-2 mt-1">
                                <i class="mdi mdi-24px mdi-clock-outline"></i>
                            </span>
                            {{ $t('Import.time_machines') }}
                        </p>
                        <div class="card-content">
                            <div
                                class="
                                    notification
                                    is-info is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{
                                        $t('Import.time_machines_desc', {
                                            num: timeMachines,
                                        })
                                    }}
                                </p>
                            </div>

                            <div
                                class="
                                    notification
                                    is-danger is-light is-fullwidth
                                "
                            >
                                <p>
                                    {{ $t('Import.not_possible_to_import') }}
                                </p>
                            </div>
                        </div>
                        <div class="card-footer px-4 py-4">
                            <div class="level is-fullwidth">
                                <div class="level-left">
                                    <div class="level-item">
                                        <p>
                                            {{ $t('Import.process_objects') }}
                                        </p>
                                    </div>
                                </div>
                                <div class="level-right">
                                    <div class="level-item">
                                        <div
                                            class="
                                                buttons
                                                has-addons
                                                pb-2
                                                disabled
                                            "
                                        >
                                            <button
                                                class="button is-disabled"
                                                disabled
                                                :class="{
                                                    'is-primary is-selected':
                                                        importTimeMachines,
                                                }"
                                                @click="
                                                    importTimeMachines = true
                                                "
                                            >
                                                {{ $t('Import.yes') }}
                                            </button>
                                            <button
                                                class="button is-disabled"
                                                disabled
                                                :class="{
                                                    'is-primary is-selected':
                                                        !importTimeMachines,
                                                }"
                                                @click="
                                                    importTimeMachines = false
                                                "
                                            >
                                                {{ $t('Import.no') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="columns is-multiline is-centered mb-6" v-else>
                <div v-if="totalObjects > 0" class="column is-5">
                    <div class="card">
                        <p
                            class="
                                card-header
                                has-background-primary has-text-white
                                px-4
                                py-4
                                has-text-weight-bold
                                is-size-5
                            "
                        >
                            <span class="icon mr-2 mt-1">
                                <i class="mdi mdi-24px mdi-map"></i>
                            </span>
                            {{
                                $t('Import.project', {
                                    projectType: getProjectType(projectType),
                                })
                            }}
                        </p>
                        <div class="card-content">
                            <div
                                class="
                                    notification
                                    is-info is-light is-fullwidth
                                "
                            >
                                <p
                                    v-html="
                                        $t('Import.object_desc', {
                                            num: totalObjects,
                                            projectType:
                                                getProjectType(projectType),
                                        })
                                    "
                                />
                            </div>

                            <p v-if="projectCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span
                                    v-html="
                                        $t('Import.project_created', {
                                            name: scenarioName,
                                        })
                                    "
                                />
                            </p>
                            <p v-else>
                                {{ $t('Import.project_to_create') }}
                                <span class="is-italic">{{
                                    scenarioName
                                }}</span>
                            </p>

                            <p v-if="objectsCreated">
                                <span
                                    class="icon is-small has-text-primary mr-1"
                                >
                                    <i class="mdi mdi-18px mdi-check-bold"></i>
                                </span>
                                <span class="mr-1">{{ totalObjects }}</span
                                >{{ $t('Import.objects_created') }}
                            </p>
                            <p v-else>
                                <span
                                    v-html="
                                        $t('Import.objects_to_create', {
                                            num: totalObjects,
                                        })
                                    "
                                />
                            </p>
                        </div>
                        <div class="card-footer px-4 py-4">
                            <div
                                class="level is-fullwidth"
                                v-if="!processingObjects"
                            >
                                <div class="level-left">
                                    <div class="level-item">
                                        <p>
                                            {{ $t('Import.process_objects') }}
                                        </p>
                                    </div>
                                </div>
                                <div class="level-right">
                                    <div class="level-item">
                                        <div class="buttons has-addons pb-2">
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        importObjects,
                                                }"
                                                @click="importObjects = true"
                                            >
                                                {{ $t('Import.yes') }}
                                            </button>
                                            <button
                                                class="button"
                                                :class="{
                                                    'is-primary is-selected':
                                                        !importObjects,
                                                }"
                                                @click="importObjects = false"
                                            >
                                                {{ $t('Import.no') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else
                                class="py-4 px-2 is-fullwidth"
                                style="width: 100%"
                            >
                                <progress
                                    class="progress is-small"
                                    :value="objectsProcessed"
                                    max="100"
                                >
                                    20%
                                </progress>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="columns" :class="{ 'is-centered': isLabylok }">
                <div class="column" :class="isLabylok ? 'is-5' : 'is-12'">
                    <button
                        class="button is-primary is-fullwidth"
                        :class="{
                            'is-loading is-disabled': importProcesStarted,
                        }"
                        @click.stop="startImport"
                    >
                        {{ $t('Import.start') }}
                    </button>
                </div>
            </div>
        </section>

        <section class="section is-small step4" v-if="importFinished">
            <p class="is-size-5 has-text-weight-bold pb-6">Proyectos creados</p>
            <div class="columns is-multiline is-mobile mx-0">
                <router-link
                    v-for="project in projects"
                    :key="project._id"
                    :to="{
                        name: 'project',
                        params: { projectId: project._id },
                    }"
                    tag="div"
                    class="column is-3"
                >
                    <project-card :content="project" />
                </router-link>
            </div>
        </section>
    </div>
</template>
<script>
import _ from 'underscore';
import ProjectCard from '@/components/ProjectCard';
import { AVAILIABLE_IMPORT_ENVIRONMENTS } from '@/lib/constants';
import {
    fetchAvailiablesScenarios,
    fetchScenarioObjectsList,
    uploadImageFromUrl,
    importObject,
    linkObjects,
} from '@/services/API';
import InfiniteLoading from 'vue-infinite-loading';
import AppSearchBar from '@/components/AppSearchBar';
import { Document } from 'flexsearch';
export default {
    components: {
        InfiniteLoading,
        AppSearchBar,
        ProjectCard,
    },
    props: {},
    data() {
        return {
            environment: '',
            environmentUrl: '',
            scenario: '',
            scenarioName: '',
            scenarioData: {},
            confirmed: false,
            //availableEnvironments: AVAILIABLE_IMPORT_ENVIRONMENTS,
            availiableScenarios: [],
            visibleAvailableScenarios: [],
            index: 10,
            busy: false,
            searchValue: '',
            points: 0,
            trivials: 0,
            tracks: 0,
            clueGames: 0,
            timeMachines: 0,
            totalObjects: 0,
            availableObjectsType: undefined,
            objectsData: [],
            projectCreated: false,
            pointsCreated: false,
            projectQuizCreated: false,
            trivialsCreated: false,
            projectTreasurehuntCreated: false,
            clueGamesCreated: false,
            importPoints: true,
            importClues: false,
            importTrivials: false,
            importTimeMachines: false,
            importTracks: false,
            importProcesStarted: false,
            prcessingPoints: false,
            pointsProcessed: 0,
            pointsFinished: 0,
            lastPointProcessed: false,
            projects: [],
            processingTrivials: false,
            trivialsProcessed: 0,
            trivialsFinished: 0,
            // objects: 0,
            objectsCreated: false,
            objectsProcessed: 0,
            importObjects: true,
            processingObjects: false,
            objectsFinished: 0,
            objectsToLink: [],
            processingClues: false,
            cluesProcessed: 0,
            cluesFinished: 0,
        };
    },

    computed: {
        searchIndex() {
            if (this.environmentUrl.indexOf('labylok') !== -1) {
                return new Document({
                    resolution: 3,
                    tokenize: 'full',
                    document: {
                        id: '_id',
                        index: [
                            {
                                field: '_id',
                                minlength: 0,
                            },
                            {
                                field: 'title',
                                minlength: 3,
                            },
                            {
                                field: 'owner:username',
                                minlength: 3,
                            },
                        ],
                    },
                });
            } else {
                return new Document({
                    resolution: 3,
                    tokenize: 'full',
                    document: {
                        id: 'id',
                        index: [
                            {
                                field: 'id',
                                minlength: 0,
                            },
                            {
                                field: 'title',
                                minlength: 3,
                            },
                        ],
                    },
                });
            }
        },
        filteredItems() {
            let items;
            this.searchIndex.remove(this.availiableScenarios);
            items = this.availiableScenarios;
            if (this.actualState) {
                this.actualState.reset();
            }
            if (this.searchValue) {
                this.searchIndex.add(items);
                const result = this.searchIndex.search(this.searchValue);
                // console.log('result', result);
                const ids = _.chain(result).pluck('result').flatten().value();
                let foundData;
                if (this.isLabylok) {
                    foundData = this.availiableScenarios.filter((item) => {
                        return _.contains(ids, item._id);
                    });
                } else {
                    foundData = this.availiableScenarios.filter((item) => {
                        return _.contains(ids, item.id);
                    });
                }

                return foundData;
            }

            return items;
        },
        importFinished() {
            return (
                this.pointsCreated &&
                this.trivialsCreated &&
                this.clueGamesCreated &&
                this.objectsCreated
            );
        },
        availableEnvironments() {
            return _.reject(
                AVAILIABLE_IMPORT_ENVIRONMENTS,
                function (importEnv) {
                    return (
                        importEnv.name ===
                        process.env.VUE_APP_IMPORT_ENVIRONMENT_NAME
                    );
                }
            );
        },
        isLabylok() {
            // console.log('isLabylok', this.environmentUrl.indexOf('labylok'));
            if (this.environmentUrl.indexOf('labylok') !== -1) {
                return true;
            }
            return false;
        },
        isDevEduloc() {
            return this.environment == 'dev.eduloc';
        },
    },
    watch: {
        availiableScenarios: {
            handler: function () {
                // console.log('availableScenarios', this.availiableScenarios);
                this.availiableScenarios.forEach((item) => {
                    this.searchIndex.add(item);
                    // console.log(this.searchIndex);
                });
            },
            immediate: true,
        },
    },
    methods: {
        searchScenarios(text) {
            // console.log('searching', text);
            this.searchValue = text;
            this.visibleAvailableScenarios = [];
            let result = this.filteredItems;
            // console.log('result', result);
            if (this.filteredItems.length) {
                const temp = [];
                for (
                    let i = this.visibleAvailableScenarios.length;
                    i <= this.visibleAvailableScenarios.length + 12;
                    i++
                ) {
                    if (this.filteredItems[i]) {
                        temp.push(this.filteredItems[i]);
                    }
                }
                this.visibleAvailableScenarios =
                    this.visibleAvailableScenarios.concat(temp);
            }
        },
        cardBackground(imageUrl) {
            return (
                "background-image: url('" +
                imageUrl +
                "'); background-size: cover; background-repeat: no-repeat; background-position: center center"
            );
        },
        async selectEnvironment(content) {
            this.environment = content.name;
            this.environmentUrl = content.url;
            let response = await fetchAvailiablesScenarios(content.name).catch(
                (err) => {
                    return this.$emit('error', err);
                }
            );
            if (response.data) {
                this.availiableScenarios = response.data;
                const temp = [];
                for (
                    let i = this.visibleAvailableScenarios.length;
                    i <= this.visibleAvailableScenarios.length + 12;
                    i++
                ) {
                    if (this.filteredItems[i]) {
                        temp.push(this.filteredItems[i]);
                    }
                }
                this.visibleAvailableScenarios =
                    this.visibleAvailableScenarios.concat(temp);
            }
        },
        async selectScenario(content) {
            this.scenario = content.id || content._id;
            this.scenarioName = content.title;
            this.scenarioData = content;
            if (!this.scenarioData.description) {
                this.scenarioData.description = 'imported scenario';
            }
            // console.log(content._id);
            let response = await fetchScenarioObjectsList(
                this.environment,
                this.scenario
            ).catch((err) => {
                return this.$emit('error', err);
            });
            if (response.data) {
                // console.log('objects', response.data);
                this.objectsData = response.data;
                this.availableObjectsType = response.data.enabledObjects;
                this.points = response.data.points
                    ? response.data.points.length
                    : 0;
                this.trivials = response.data.trivials
                    ? response.data.trivials.length
                    : 0;
                this.clueGames = response.data.clueGames
                    ? response.data.clueGames.length
                    : 0;
                this.timeMachines = response.data.timeMachines
                    ? response.data.timeMachines.length
                    : 0;
                this.tracks = response.data.tracks
                    ? response.data.tracks.length
                    : 0;
                this.totalObjects = response.data.objects.length;
                this.projectType = response.data.projectType;
                // console.log('total objects', this.totalObjects);
            }
        },
        goToScenario(scenarioId) {
            if (this.environment == 'eduloc') {
                return (
                    'http://www.eduloc.net/' +
                    this.$i18n.locale +
                    '/escenari/' +
                    scenarioId +
                    '/preview-iframe'
                );
            } else if (this.isLabylok) {
                return this.environmentUrl + '/project/' + scenarioId;
            } else if (this.environment == 'dev.eduloc') {
                return (
                    'http://dev.eduloc.net/' +
                    this.$i18n.locale +
                    '/escenari/' +
                    scenarioId +
                    '/mostra'
                );
            }
        },
        infiniteHandler($state) {
            if (
                this.visibleAvailableScenarios.length >=
                this.filteredItems.length
            ) {
                $state.complete();
            }
            setTimeout(() => {
                const temp = [];
                for (
                    let i = this.visibleAvailableScenarios.length;
                    i <= this.visibleAvailableScenarios.length + 12;
                    i++
                ) {
                    if (this.filteredItems[i]) {
                        temp.push(this.filteredItems[i]);
                    }
                }
                this.visibleAvailableScenarios =
                    this.visibleAvailableScenarios.concat(temp);
                $state.loaded();
            }, 1000);
            this.actualState = $state;
        },
        startImport() {
            // console.log('start import');
            this.importProcesStarted = true;

            if (this.importPoints && this.objectsData.points) {
                let activeScenario = {};
                // console.log('start import points process');
                let increment = 100 / this.objectsData.points.length;
                // create scenario
                // { type, title, description, mode }
                // console.log(this.scenarioData);
                this.prcessingPoints = true;
                let projectImage = uploadImageFromUrl(
                    this.environment,
                    this.scenarioData.image
                )
                    .catch((err) => {
                        return this.$emit('error', err);
                    })
                    .then((imageData) => {
                        let response = this.$store
                            .dispatch('createProjectForImport', {
                                type: 'collection',
                                title: this.scenarioData.title,
                                description: this.scenarioData.description,
                                mode: 'map',
                                image: imageData.data,
                            })
                            .then((response) => {
                                activeScenario = response.data;
                                // console.log('project created');

                                this.projectCreated = true;

                                for (let pointData of this.objectsData.points) {
                                    // console.log(pointData);
                                    let importedPoint = importObject(
                                        this.environment,
                                        {
                                            project: activeScenario,
                                            objectId: pointData,
                                        }
                                    )
                                        .catch((err) => {
                                            return this.$emit('error', err);
                                        })
                                        .then((newObject) => {
                                            //console.log(newObject.data);
                                            this.pointsProcessed =
                                                this.pointsProcessed +
                                                increment;
                                            this.pointsFinished++;

                                            if (
                                                this.pointsFinished ===
                                                this.objectsData.points.length
                                            ) {
                                                //this.importProcesStarted = false;
                                                this.projects.push(
                                                    activeScenario
                                                );
                                                setTimeout(() => {
                                                    this.objectsCreated = true;
                                                    this.pointsCreated = true;
                                                    if (
                                                        this.importClues ===
                                                        false
                                                    ) {
                                                        this.clueGamesCreated = true;
                                                    }
                                                    if (
                                                        this.importTrivials ==
                                                        false
                                                    ) {
                                                        this.trivialsCreated = true;
                                                    }
                                                }, 1000);
                                            }
                                        });
                                }
                            })
                            .catch((e) => {
                                console.log('catch', e);
                            })
                            .finally(() => {
                                // console.log('active scenario', activeScenario);
                            });
                    });
            }
            if (this.importTrivials && this.objectsData.trivials) {
                let quizScenario = {};
                // console.log('start import trivials process');
                let increment = 100 / this.objectsData.trivials.length;
                // create scenario
                // { type, title, description, mode }
                // console.log(this.scenarioData);
                this.processingTrivials = true;
                let projectImage = uploadImageFromUrl(
                    this.environment,
                    this.scenarioData.image
                )
                    .catch((err) => {
                        return this.$emit('error', err);
                    })
                    .then((imageData) => {
                        let response = this.$store
                            .dispatch('createProjectForImport', {
                                type: 'trivial',
                                title: this.scenarioData.title,
                                description: this.scenarioData.description,
                                mode: 'map',
                                image: imageData.data,
                            })
                            .then((response) => {
                                quizScenario = response.data;
                                // console.log('project created');

                                this.projectQuizCreated = true;

                                for (let trivialData of this.objectsData
                                    .trivials) {
                                    // console.log(pointData);
                                    let importedTrivial = importObject(
                                        this.environment,
                                        {
                                            project: quizScenario,
                                            objectId: trivialData,
                                        }
                                    )
                                        .catch((err) => {
                                            return this.$emit('error', err);
                                        })
                                        .then((newObject) => {
                                            //console.log(newObject.data);
                                            this.trivialsProcessed =
                                                this.trivialsProcessed +
                                                increment;
                                            this.trivialsFinished++;

                                            if (
                                                this.trivialsFinished ===
                                                this.objectsData.trivials.length
                                            ) {
                                                this.importProcesStarted = false;
                                                this.projects.push(
                                                    quizScenario
                                                );
                                                setTimeout(() => {
                                                    this.objectsCreated = true;
                                                    this.trivialsCreated = true;
                                                    if (
                                                        this.importClues ===
                                                        false
                                                    ) {
                                                        this.clueGamesCreated = true;
                                                    }
                                                    if (
                                                        this.importPoints ==
                                                        false
                                                    ) {
                                                        this.pointsCreated = true;
                                                    }
                                                }, 1000);
                                            }
                                        });
                                }
                            })
                            .catch((e) => {
                                console.log('catch', e);
                            })
                            .finally(() => {
                                // console.log('active scenario', activeScenario);
                            });
                    });
            }
            if (this.importClues && this.objectsData.clueGames) {
                // console.log('start import trivials process');
                let increment = 100 / this.objectsData.clueGames.length;
                // create scenario
                // { type, title, description, mode }
                // console.log(this.scenarioData);
                let clueScenario = {};
                this.processingClues = true;
                let projectImage = uploadImageFromUrl(
                    this.environment,
                    this.scenarioData.image
                )
                    .catch((err) => {
                        return this.$emit('error', err);
                    })
                    .then((imageData) => {
                        for (let clueData of this.objectsData.clueGames) {
                            // console.log(pointData);
                            let response = this.$store
                                .dispatch('createProjectForImport', {
                                    type: 'treasurehunt',
                                    title:
                                        this.scenarioData.title +
                                        '-' +
                                        clueData,
                                    description: this.scenarioData.description,
                                    image: imageData.data,
                                })
                                .then((response) => {
                                    clueScenario = response.data;
                                    this.projects.push(clueScenario);
                                    // console.log('project created');

                                    this.projectTreasurehuntCreated = true;

                                    let importedClue = importObject(
                                        this.environment,
                                        {
                                            project: clueScenario,
                                            objectId: clueData,
                                        }
                                    )
                                        .catch((err) => {
                                            return this.$emit('error', err);
                                        })
                                        .then((newObject) => {
                                            //console.log(newObject.data);
                                            this.cluesProcessed =
                                                this.cluesProcessed + increment;
                                            this.cluesFinished++;

                                            if (
                                                this.cluesFinished ===
                                                this.objectsData.clueGames
                                                    .length
                                            ) {
                                                this.importProcesStarted = false;

                                                setTimeout(() => {
                                                    this.objectsCreated = true;
                                                    this.clueGamesCreated = true;
                                                    if (
                                                        this.importTrivials ===
                                                        false
                                                    ) {
                                                        this.trivialsCreated = true;
                                                    }
                                                    if (
                                                        this.importPoints ==
                                                        false
                                                    ) {
                                                        this.pointsCreated = true;
                                                    }
                                                }, 1000);
                                            }
                                        });
                                })
                                .catch((e) => {
                                    console.log('catch', e);
                                })
                                .finally(() => {
                                    // console.log('active scenario', activeScenario);
                                });
                        }
                    });
            }

            if (this.importObjects && this.objectsData.objects) {
                let activeScenario = {};
                // console.log('start import objects process');
                let increment = 100 / this.objectsData.objects.length;
                // create scenario
                // { type, title, description, mode }
                // console.log(this.scenarioData);
                this.processingObjects = true;
                /* Duplicate project
                 * {projectData, content}
                 * projectData => project data
                 * content => true or false / duplicate content of original project
                 */
                let response = this.$store
                    .dispatch('duplicateProject', {
                        projectData: this.scenarioData,
                        content: false,
                    })
                    .then((response) => {
                        activeScenario = response.data;
                        // console.log('project created');

                        this.projectCreated = true;

                        for (let objectData of this.objectsData.objects) {
                            let importedObject = importObject(
                                this.environment,
                                {
                                    project: activeScenario,
                                    objectId: objectData._id,
                                }
                            )
                                .catch((err) => {
                                    return this.$emit('error', err);
                                })
                                .then((newObject) => {
                                    // console.log(newObject.data[0]._id);
                                    this.objectsToLink.push({
                                        newId: newObject.data[0]._id,
                                        oldId: objectData._id,
                                    });
                                    this.objectsProcessed =
                                        this.objectsProcessed + increment;
                                    this.objectsFinished++;
                                    if (
                                        this.objectsFinished ===
                                        this.objectsData.objects.length
                                    ) {
                                        /*console.log(
                                            'finishing process for',
                                            activeScenario
                                        );*/
                                        //this.importProcesStarted = false;
                                        if (
                                            activeScenario.type ==
                                            'treasurehunt'
                                        ) {
                                            console.log(
                                                'linking objects',
                                                this.objectsToLink
                                            );
                                            let linkedObjects = linkObjects({
                                                project: activeScenario,
                                                objects: this.objectsToLink,
                                            })
                                                .catch((err) => {
                                                    return this.$emit(
                                                        'error',
                                                        err
                                                    );
                                                })
                                                .then((objectsLinked) => {
                                                    this.projects.push(
                                                        activeScenario
                                                    );
                                                    setTimeout(() => {
                                                        this.objectsCreated = true;
                                                        this.pointsCreated = true;
                                                        this.trivialsCreated = true;
                                                        this.clueGamesCreated = true;
                                                        this.objectsToLink = [];
                                                    }, 1000);
                                                });
                                        } else {
                                            this.projects.push(activeScenario);
                                            setTimeout(() => {
                                                this.objectsCreated = true;
                                                this.pointsCreated = true;
                                                this.trivialsCreated = true;
                                                this.clueGamesCreated = true;
                                            }, 1000);
                                        }
                                    }
                                });
                        }
                    })
                    .catch((e) => {
                        console.log('catch', e);
                    })
                    .finally(() => {
                        // console.log('active scenario', activeScenario);
                    });
            }
        },
        getImageUrl(scenario) {
            if (scenario.image && scenario.image.url) {
                return scenario.image.url;
            } else if (
                scenario.backgroundImage &&
                scenario.backgroundImage.url
            ) {
                return scenario.backgroundImage.url;
            } else if (
                scenario.background &&
                scenario.background.type == 'unsplash'
            ) {
                return (
                    scenario.background.value.urls.small ||
                    scenario.background.value.urls.regular
                );
            } else {
                return process.env.VUE_APP_FRONT_URL + '/img/empty.png';
            }
        },
        getProjectType(projectType) {
            return this.$t('Import.project_type_' + projectType);
        },
    },
    mounted() {
        this.loading = false;
    },
    beforeDestroy() {},
};
</script>
<style scooped>
ul {
    list-style: none;
}
.is-clipped {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;
}
.is-clipped-1 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
}
.scenario-card .card-content {
    height: 10rem;
    max-height: 10rem;
}

.spinner {
    margin: 0 auto;
    width: 110px;
    text-align: center;
}

.spinner > div {
    width: 18px;
    height: 18px;
    background-color: #0069bb;

    border-radius: 100%;
    display: inline-block;
    -webkit-animation: sk-bouncedelay 1.4s infinite ease-in-out both;
    animation: sk-bouncedelay 1.4s infinite ease-in-out both;
}

.spinner .bounce1 {
    -webkit-animation-delay: -0.8s;
    animation-delay: -0.8s;
}

.spinner .bounce2 {
    -webkit-animation-delay: -0.64s;
    animation-delay: -0.64s;
}

.spinner .bounce3 {
    -webkit-animation-delay: -0.48s;
    animation-delay: -0.48s;
}

.spinner .bounce4 {
    -webkit-animation-delay: -0.32s;
    animation-delay: -0.32s;
}

.spinner .bounce5 {
    -webkit-animation-delay: -0.16s;
    animation-delay: -0.16s;
}

@-webkit-keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
        -webkit-transform: scale(0);
    }
    40% {
        -webkit-transform: scale(1);
    }
}

@keyframes sk-bouncedelay {
    0%,
    80%,
    100% {
        -webkit-transform: scale(0);
        transform: scale(0);
    }
    40% {
        -webkit-transform: scale(1);
        transform: scale(1);
    }
}

.notification.is-fullwidth {
    width: 100%;
}
.step3 .card-content {
    min-height: 18rem;
}
.step3 .level {
    width: 100%;
}
</style>
